import { formatStatus } from "Pages/GcPrecon/GcProjectDetails/components/DrawHistoryTable";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import TableSkeleton from "Components/Common/Skeleton/TableSkeleton";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import EmptyDrawHistoryIcon from "Assets/Img/EmptyDrawHistoryIcon";
import Table, { Column } from "Components/Table/Table";
import Button from "Components/Common/Button/Button";

interface AmountField {
  amount: number;
  currency: { code: string; symbol: string; name: string };
}

export interface Draw {
  id: string;
  toDate: string;
  retainageAmount: AmountField | null;
  retainageThreshold: string | null;
  status: string;
  approvedAmount: AmountField | null;
  requestedAmount: AmountField;
  ownerSignatureUrl: string | null | Record<string, string>;
  drawNumber: number;
}

export interface DrawSummary {
  totalProjectCost: AmountField;
  totalDisbursed: AmountField;
  pendingDraw: AmountField;
  balanceToFinish: AmountField;
}

interface Props {
  draws: Draw[];
  summary: DrawSummary | null;
  onSignNow: () => void;
  loading?: boolean;
}

const fmt = (field: AmountField | null) =>
  field
    ? `${field?.currency?.symbol ?? ""}${field?.amount?.toLocaleString() ?? ""}`
    : "—";

const STATUS_LABELS: Record<string, { label: string; cls: string }> = {
  owner_signature_pending: { label: "Awaiting Sig.", cls: "b-pending" },
  approved: { label: "Approved", cls: "b-approved" },
  submitted: { label: "Submitted", cls: "b-pending" },
  rejected: { label: "Rejected", cls: "b-pending" },
};

export default function OwnerDrawHistorySection({
  draws,
  summary,
  onSignNow,
  loading,
}: Props) {
  const pendingDraw = draws.find((d) => d.status === "owner_signature_pending");
  const retainagePct = draws.find(
    (d) => d.retainageThreshold,
  )?.retainageThreshold;
  const retainageHeader = retainagePct
    ? `Retainage (${parseFloat(retainagePct)}%)`
    : "Retainage";

  if (loading) {
    return (
      <>
        <div className="ownerDrawSkelGrid">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="card ownerDrawSkelCard">
              <Skeleton height={12} width="50%" className="ownerDrawSkelLine1" />
              <Skeleton height={22} width="70%" className="ownerDrawSkelLine2" />
              <Skeleton height={11} width="40%" />
            </div>
          ))}
        </div>
        <div className="card">
          <Skeleton height={16} width="30%" className="ownerDrawSkelTableTitle" />
          <TableSkeleton cols={5} rows={4} />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="section-title">Draw History</div>
      <div className="section-sub">All draw requests for this project</div>

      <div className="card">
        <div className="card-title">Draw Summary</div>
        <div className="ownerDrawSummary">
          <div className="ods-card">
            <div className="ods-label">Total Disbursed</div>
            <div className="ods-value blue">
              {summary ? fmt(summary.totalDisbursed) : "—"}
            </div>
          </div>
          <div className="ods-card pending">
            <div className="ods-label pending">
              {pendingDraw
                ? `Pending — Draw #${pendingDraw.drawNumber}`
                : "Pending"}
            </div>
            <div className="ods-value pending">
              {summary ? fmt(summary.pendingDraw) : "—"}
            </div>
          </div>
          <div className="ods-card">
            <div className="ods-label">Balance to Finish</div>
            <div className="ods-value">
              {summary ? fmt(summary.balanceToFinish) : "—"}
            </div>
          </div>
        </div>

        <Table
          tableClassName="draw-table"
          data={draws}
          columns={
            [
              {
                header: "Draw #",
                render: (draw) => (
                  <span className="draw-num">
                    Draw {draw.drawNumber.toString().padStart(2, "0")}
                  </span>
                ),
              },
              {
                header: "Period To",
                render: (draw) =>
                  new Date(draw.toDate).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  }),
              },
              {
                header: "Requested",
                render: (draw) => fmt(draw.requestedAmount),
              },
              {
                header: retainageHeader,
                render: (draw) => fmt(draw.retainageAmount),
              },
              {
                header: "Net",
                render: (draw) => {
                  const net =
                    draw.requestedAmount.amount -
                    (draw.retainageAmount?.amount ?? 0);
                  return `${draw.requestedAmount.currency.symbol}${net.toLocaleString()}`;
                },
              },
              {
                header: "Status",
                render: (draw) => {
                  const s = STATUS_LABELS[draw.status] ?? {
                    label: draw.status,
                    cls: "b-pending",
                  };
                  return (
                    <span className={`badge ${s.cls}`}>
                      <span className="bdot" />
                      {formatStatus(s.label)}
                    </span>
                  );
                },
              },
              {
                header: "Action",
                render: (draw) =>
                  draw.status === "owner_signature_pending" ? (
                    <Button className="btn btn-sign btn-sm" onClick={onSignNow}>
                      Sign Now
                    </Button>
                  ) : (
                    <span className="badge b-signed">
                      <span className="bdot" />
                      Signed
                    </span>
                  ),
              },
            ] as Column<Draw>[]
          }
          fallbackUI={
            <EmptyState
              noBorder
              icon={<EmptyDrawHistoryIcon />}
              title="No Draws on Record"
              subtitle="Draw requests submitted for this project will appear here once the first disbursement is initiated."
            />
          }
        />
      </div>
    </>
  );
}
